import { sortByName, sortByRefined } from './sorters';

/**
 * Mandatory options: content, el, facets, hitCount, tmpl
 * @param {Object} options
 */
export default class Facets {
  constructor(options) {
    Object.assign(this, options);
    this.html = '';
  }

  /**
   * Takes a facet props object and does some final massaging
   * before passing it to the template for rendering
   * @param {Object} props
   */
  buildFacet(props) {
    const { values } = props;

    props.values = values.slice(0, 25);
    props.has_other_values = values.length > 25;
    props.other_values = values.slice(25);

    this.html += this.tmpl.render(props);
  }

  /**
   * Takes a facet config object and returns a sorting function
   * later used by Algolia to sort facets
   * @param {Object} config
   * @returns {Function}
   */
  getSortFunction(config) {
    let sortFunction = config.sortFunction || sortByName;

    if (config.topListIfRefined) {
      sortFunction = sortByRefined(sortFunction);
    }

    return sortFunction;
  }

  /**
   * Tells Algolia to refine a search based on the facet name/value
   * from the event target's dataset
   * @param {Event} e
   * @returns {Boolean}
   */
  handleClick(e) {
    const { facet, value } = e.target.dataset;

    this.helper
      .toggleRefine(facet, value)
      .search();

    return false;
  }

  /**
   * Merges Algolia's given facet config object with our own
   * properties before passing the object on for rendering
   * @param {Object} facetConfig
   */
  process(facetConfig) {
    const { disjunctive, name, title, type } = facetConfig;
    const data = this.content.getFacetValues(name);
    const isRefined = this.helper.hasRefinements(name);
    const sortFunction = this.getSortFunction(facetConfig);
    const values = this.processFacetValues(data, sortFunction);

    this.buildFacet({
      disjunctive,
      facet: name,
      isRefined,
      title,
      type,
      values
    });
  }

  /**
   * Takes a facet's values, adds new props, then sorts them
   * @param {Array} data
   * @param {Function} sortFunction
   * @returns {Array}
   */
  processFacetValues(data, sortFunction) {
    const values = data.map((facet, id) => {
      const formattedName = facet.name.replace(' ', '-').toLowerCase();
      const newValues = {
        id: `${formattedName}-${id}`,
        label: facet.name,
        labelClass: formattedName,
        refined: facet.isRefined,
        value: facet.name
      };

      delete facet.isRefined;

      return Object.assign(facet, newValues);
    });

    return values.sort(sortFunction);
  }

  /**
   * Called by the renderer to get the ball rolling. Renders all
   * facets, and adds/removes listeners to handle facet refining
   */
  render() {
    this.el.off('click', '.toggleRefine');

    if (this.hitCount === 0) {
      this.el.empty();
      return;
    }

    this.facets.forEach(this.process.bind(this));
    this.el.html(this.html);
    this.el.on('click', '.toggleRefine', this.handleClick.bind(this));
  }
}
